// Where the light reflects off a card: a soft light in front of the deck (up
// and to the side of the camera), seen in the card as in a mirror.
//
// In the plane of the card's turn (the card's width when it turns over its
// vertical axis, its height for the horizontal one): the card is a line
// through the origin turned by its angle, its camera P in front of it (as the
// page draws it: CSS perspective(P), its own camera per card). For points along
// the card, the view ray is reflected about the card's normal; where the
// reflected ray points at the light, the light shows. As the card turns, that
// spot sweeps across it (twice as fast as the card turns, as in a mirror), and
// flares as the card catches the light head on.

export type Glint = {
    /** where on the card the light shows: 0..1 across the turning direction */
    at: number
    /** how bright (0..1), seen on the side facing the camera */
    strength: number
    /** the angle between the reflected ray and the light (deg): the hologram's colour shift */
    hue: number
}

/** light direction (deg from the camera's axis, in the turn's plane) and its spread */
const LIGHT = { angle: -28, spread: 13 }

export function glint(turnDeg: number, persp: number, half: number): Glint {
    const th = (turnDeg * Math.PI) / 180
    // the side of the card facing the camera: its normal turned towards it
    let nx = -Math.sin(th),
        nz = Math.cos(th)
    if (nz < 0) ((nx = -nx), (nz = -nz))
    const lx = Math.sin((LIGHT.angle * Math.PI) / 180),
        lz = Math.cos((LIGHT.angle * Math.PI) / 180)
    let best = -1,
        at = 0.5,
        hue = 0
    const N = 48
    for (let k = 0; k <= N; k++) {
        const u = (k / N) * 2 - 1
        // the point on the card, and the ray from the camera to it
        const px = u * half * Math.cos(th),
            pz = u * half * Math.sin(th)
        let vx = px,
            vz = pz - persp
        const len = Math.hypot(vx, vz)
        vx /= len
        vz /= len
        // reflected about the normal
        const d = vx * nx + vz * nz
        const rx = vx - 2 * d * nx,
            rz = vz - 2 * d * nz
        // its angle to the light
        const off = (Math.acos(Math.max(-1, Math.min(1, rx * lx + rz * lz))) * 180) / Math.PI
        const s = Math.exp(-((off / LIGHT.spread) ** 2))
        if (s > best) ((best = s), (at = (u + 1) / 2), (hue = off))
    }
    // a little more at a grazing view (Fresnel), never quite none
    const facing = Math.abs(nz)
    const fresnel = 0.75 + 0.25 * (1 - facing) ** 2
    return { at, strength: Math.max(0, Math.min(1, best * fresnel)), hue }
}
